import Phaser from "phaser";
import type { LaneNumber } from "../../../../shared/types/CommanderDecision";
import type { ObjectiveRiskLevel, ObjectiveStateSummary } from "../../../../shared/types/BattleEvent";
import mapConfig from "../data/mapConfig.json";
import { Unit } from "../entities/Unit";
import { EconomySystem } from "./EconomySystem";

export type ObjectiveReward = "gold" | "points" | "buff" | "random";

export type LaneObjective = {
  id: string;
  lane: LaneNumber;
  x: number;
  y: number;
  reward: ObjectiveReward;
  riskLevel: ObjectiveRiskLevel;
  rewardTier: 1 | 2 | 3;
  captureMs: number;
  captureProgressMs: number;
  captureRadius: number;
  capturePenaltyMultiplier: number;
  expiresAt: number;
  marker: Phaser.GameObjects.Container;
  progressFill: Phaser.GameObjects.Rectangle;
  timerLabel: Phaser.GameObjects.Text;
};

export type ObjectiveCallbacks = {
  onSpawned?: (objective: LaneObjective) => void;
  onCompleted?: (objective: LaneObjective, reward: Exclude<ObjectiveReward, "random">) => void;
  onExpired?: (objective: LaneObjective) => void;
};

export class ObjectiveSystem {
  private nextObjectiveAt = 9000;
  private readonly objectives: LaneObjective[] = [];
  private completed = 0;
  private expired = 0;
  private lastUpdateAt = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly economy: EconomySystem,
    private readonly callbacks: ObjectiveCallbacks = {}
  ) {}

  update(time: number, playerUnits: Unit[]) {
    const elapsedMs = Math.min(250, Math.max(0, time - this.lastUpdateAt));
    this.lastUpdateAt = time;

    if (time >= this.nextObjectiveAt) {
      this.spawnObjective(time);
      this.nextObjectiveAt = time + Phaser.Math.Between(14000, 22000);
    }

    for (let index = this.objectives.length - 1; index >= 0; index -= 1) {
      const objective = this.objectives[index];
      if (!objective) continue;
      const capturers = playerUnits.filter(
        (unit) => unit.alive && unit.lane === objective.lane && Math.abs(unit.x - objective.x) <= objective.captureRadius
      );
      if (capturers.length > 0) {
        const captureSpeed = Math.min(1.8, 1 + (capturers.length - 1) * 0.28);
        objective.captureProgressMs = Math.min(objective.captureMs, objective.captureProgressMs + elapsedMs * captureSpeed);
        for (const unit of capturers) {
          unit.applyObjectiveVulnerability(time + 520, objective.capturePenaltyMultiplier);
        }
      } else {
        objective.captureProgressMs = Math.max(0, objective.captureProgressMs - elapsedMs * 0.42);
      }

      this.updateMarker(objective, time);

      if (objective.captureProgressMs >= objective.captureMs) {
        const reward = this.resolveReward(objective.reward);
        this.applyReward(reward, playerUnits, objective.lane, objective.rewardTier);
        objective.marker.destroy();
        this.objectives.splice(index, 1);
        this.completed += 1;
        this.callbacks.onCompleted?.(objective, reward);
      } else if (time >= objective.expiresAt) {
        objective.marker.destroy();
        this.objectives.splice(index, 1);
        this.expired += 1;
        this.callbacks.onExpired?.(objective);
      }
    }
  }

  get activeLabels() {
    return this.objectives.map(
      (objective) =>
        `lane_${objective.lane}_${objective.reward}_risk_${objective.riskLevel}_tier_${objective.rewardTier}_capture_${Math.round(
          (objective.captureProgressMs / objective.captureMs) * 100
        )}`
    );
  }

  get activeDetails(): ObjectiveStateSummary[] {
    return this.objectives.map((objective) => ({
      id: objective.id,
      lane: objective.lane,
      reward: objective.reward,
      riskLevel: objective.riskLevel,
      rewardTier: objective.rewardTier,
      captureProgress: Number((objective.captureProgressMs / objective.captureMs).toFixed(2)),
      expiresInSec: Math.max(0, Math.ceil((objective.expiresAt - this.scene.time.now) / 1000)),
      x: Math.round(objective.x)
    }));
  }

  get successRate() {
    const total = this.completed + this.expired;
    return total === 0 ? 0 : this.completed / total;
  }

  private spawnObjective(time: number) {
    const lane = Phaser.Math.Between(1, 5) as LaneNumber;
    const laneConfig = mapConfig.lanes.find((item) => item.lane === lane) ?? mapConfig.lanes[2]!;
    const reward = Phaser.Utils.Array.GetRandom(["gold", "points", "buff", "random"] as ObjectiveReward[]);
    const riskLevel = this.pickRiskLevel();
    const rewardTier = this.rewardTier(riskLevel);
    const x = this.pickObjectiveX(riskLevel);
    const marker = this.createMarker(x, laneConfig.y, reward, riskLevel, rewardTier);
    const objective: LaneObjective = {
      id: `obj_${time}_${lane}`,
      lane,
      x,
      y: laneConfig.y,
      reward,
      riskLevel,
      rewardTier,
      captureMs: this.captureMs(riskLevel),
      captureProgressMs: 0,
      captureRadius: 48,
      capturePenaltyMultiplier: this.capturePenaltyMultiplier(riskLevel),
      expiresAt: time + 14000,
      marker: marker.container,
      progressFill: marker.progressFill,
      timerLabel: marker.timerLabel
    };
    objective.expiresAt = time + this.expireMs(riskLevel);
    this.objectives.push(objective);
    this.callbacks.onSpawned?.(objective);
  }

  private createMarker(x: number, y: number, reward: ObjectiveReward, riskLevel: ObjectiveRiskLevel, rewardTier: 1 | 2 | 3) {
    const container = this.scene.add.container(x, y - 42).setDepth(95);
    const glow = this.scene.add.circle(0, 0, 18, 0x5de9ff, 0.24);
    const riskHalo = this.scene.add.circle(0, 0, 27).setStrokeStyle(2, this.riskColor(riskLevel), 0.7);
    const ring = this.scene.add.circle(0, 0, 21).setStrokeStyle(2, this.rewardColor(reward), 0.9);
    const label = this.scene.add
      .text(0, -5, `${this.rewardGlyph(reward)}${rewardTier}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "18px",
        color: "#f8efc9",
        stroke: "#111016",
        strokeThickness: 3
      })
      .setOrigin(0.5);
    const progressBack = this.scene.add.rectangle(0, 21, 46, 6, 0x08070a, 0.78).setStrokeStyle(1, 0x1f2630, 0.85);
    const progressFill = this.scene.add.rectangle(-22, 21, 0, 4, this.riskColor(riskLevel), 0.96).setOrigin(0, 0.5);
    const timerLabel = this.scene.add
      .text(0, 33, "", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "13px",
        color: "#fff7d6",
        stroke: "#111016",
        strokeThickness: 3
      })
      .setOrigin(0.5);
    container.add([glow, riskHalo, ring, label, progressBack, progressFill, timerLabel]);
    this.scene.tweens.add({ targets: container, y: y - 50, yoyo: true, repeat: -1, duration: 850, ease: "Sine.easeInOut" });
    return { container, progressFill, timerLabel };
  }

  private updateMarker(objective: LaneObjective, time: number) {
    const progress = Phaser.Math.Clamp(objective.captureProgressMs / objective.captureMs, 0, 1);
    objective.progressFill.width = Math.max(1, 44 * progress);
    objective.timerLabel.setText(`${Math.ceil(Math.max(0, objective.expiresAt - time) / 1000)}s`);
    objective.marker.setAlpha(time >= objective.expiresAt - 3500 ? 0.72 + Math.sin(time / 100) * 0.2 : 1);
  }

  private rewardColor(reward: ObjectiveReward) {
    if (reward === "gold") return 0xffe58e;
    if (reward === "points") return 0x8df3ff;
    if (reward === "buff") return 0x8fff92;
    return 0xf6a5ff;
  }

  private rewardGlyph(reward: ObjectiveReward) {
    if (reward === "gold") return "G";
    if (reward === "points") return "P";
    if (reward === "buff") return "B";
    return "?";
  }

  private riskColor(riskLevel: ObjectiveRiskLevel) {
    if (riskLevel === "high") return 0xff765c;
    if (riskLevel === "medium") return 0xffcf6b;
    return 0x77f0ff;
  }

  private pickRiskLevel(): ObjectiveRiskLevel {
    const roll = Phaser.Math.Between(1, 100);
    if (roll <= 20) return "high";
    if (roll <= 56) return "medium";
    return "low";
  }

  private rewardTier(riskLevel: ObjectiveRiskLevel): 1 | 2 | 3 {
    if (riskLevel === "high") return 3;
    if (riskLevel === "medium") return 2;
    return 1;
  }

  private pickObjectiveX(riskLevel: ObjectiveRiskLevel) {
    if (riskLevel === "high") return Phaser.Math.Between(430, 555);
    if (riskLevel === "medium") return Phaser.Math.Between(555, 685);
    return Phaser.Math.Between(685, 800);
  }

  private captureMs(riskLevel: ObjectiveRiskLevel) {
    if (riskLevel === "high") return 4400;
    if (riskLevel === "medium") return 3400;
    return 2400;
  }

  private expireMs(riskLevel: ObjectiveRiskLevel) {
    if (riskLevel === "high") return 19000;
    if (riskLevel === "medium") return 17000;
    return 15000;
  }

  private capturePenaltyMultiplier(riskLevel: ObjectiveRiskLevel) {
    if (riskLevel === "high") return 1.32;
    if (riskLevel === "medium") return 1.22;
    return 1.12;
  }

  private resolveReward(reward: ObjectiveReward): Exclude<ObjectiveReward, "random"> {
    if (reward !== "random") return reward;
    return Phaser.Utils.Array.GetRandom(["gold", "points", "buff"] as const);
  }

  private applyReward(reward: Exclude<ObjectiveReward, "random">, playerUnits: Unit[], lane: LaneNumber, rewardTier: 1 | 2 | 3) {
    if (reward === "gold") {
      this.economy.reward([0, 70, 105, 145][rewardTier] ?? 70);
    } else if (reward === "points") {
      this.economy.addObjectivePoints([0, 10, 16, 24][rewardTier] ?? 10);
    } else {
      const upgradeCount = rewardTier >= 3 ? 2 : 1;
      for (const unit of playerUnits.filter((candidate) => candidate.alive && candidate.lane === lane)) {
        for (let index = 0; index < upgradeCount; index += 1) {
          unit.upgrade();
        }
      }
    }
  }
}
